import React from "react";
import "./Toggle.scss";

type ToggleBase = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** The text next to the control; with hideLabel it only names it for screen readers */
  label: React.ReactNode;
  /** A muted line under the label */
  description?: React.ReactNode;
  hideLabel?: boolean;
  disabled?: boolean;
  className?: string;
  name?: string;
  id?: string;
};

/** A square brand checkbox, orange when ticked. A real checkbox underneath. */
export function Checkbox({ checked, onChange, label, description, hideLabel = false, disabled = false, className, name, id, indeterminate = false }: ToggleBase & { indeterminate?: boolean }) {
  return (
    <label className={["ui-check", disabled ? "is-disabled" : "", className].filter(Boolean).join(" ")}>
      <input
        id={id}
        name={name}
        className="ui-check__input"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={hideLabel && typeof label === "string" ? label : undefined}
        ref={(el) => {
          if (el) el.indeterminate = indeterminate;
        }}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={`ui-check__box${indeterminate ? " is-mixed" : ""}`} aria-hidden="true" />
      <ToggleText label={label} description={description} hideLabel={hideLabel} />
    </label>
  );
}

/** An on/off switch, orange when on. A real checkbox (role switch) underneath. */
export function Switch({ checked, onChange, label, description, hideLabel = false, disabled = false, className, name, id }: ToggleBase) {
  return (
    <label className={["ui-switch", disabled ? "is-disabled" : "", className].filter(Boolean).join(" ")}>
      <input
        id={id}
        name={name}
        className="ui-switch__input"
        type="checkbox"
        role="switch"
        aria-label={hideLabel && typeof label === "string" ? label : undefined}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="ui-switch__track" aria-hidden="true">
        <span className="ui-switch__thumb" />
      </span>
      <ToggleText label={label} description={description} hideLabel={hideLabel} />
    </label>
  );
}

function ToggleText({ label, description, hideLabel }: { label: React.ReactNode; description?: React.ReactNode; hideLabel: boolean }) {
  // A plain-text hidden label names the input via aria-label instead (no stray text in table exports).
  if (hideLabel) return typeof label === "string" ? null : <span className="ui-visually-hidden">{label}</span>;
  return (
    <span className="ui-toggle-text">
      <span className="ui-toggle-text__label">{label}</span>
      {description ? <span className="ui-toggle-text__description">{description}</span> : null}
    </span>
  );
}
